// src/components/admin/SystemSettings.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchSystemSettings,
    updateSystemSettings
} from '../../features/admin/adminSlice';

const SystemSettings = () => {
    const dispatch = useDispatch();
    const { settings, isLoading, error } = useSelector(state => state.admin);

    const [formData, setFormData] = useState({
        appName: '',
        maxTasksPerProject: 100,
        enablePublicRegistration: true,
        requireEmailVerification: true,
        defaultUserRole: 'user',
        maxFileUploadSize: 5, // MB
        apiRateLimit: 100,
        enableAdvancedAnalytics: true,
        enableTeamFeatures: true,
        smtpHost: '',
        smtpPort: '',
        smtpUser: '',
        smtpPassword: '',
        emailFrom: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        dispatch(fetchSystemSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settings) {
            setFormData(settings);
        }
    }, [settings]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: parseInt(value, 10)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setSuccessMessage('');

        try {
            await dispatch(updateSystemSettings(formData)).unwrap();
            setSuccessMessage('Settings updated successfully');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Failed to update settings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="loading">Loading settings...</div>;
    }

    return (
        <div className="admin-container">
            <h1>System Settings</h1>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <form onSubmit={handleSubmit} className="settings-form">
                <div className="settings-section">
                    <h2>General Settings</h2>

                    <div className="form-group">
                        <label htmlFor="appName">Application Name</label>
                        <input
                            type="text"
                            id="appName"
                            name="appName"
                            value={formData.appName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="maxTasksPerProject">Max Tasks Per Project</label>
                            <input
                                type="number"
                                id="maxTasksPerProject"
                                name="maxTasksPerProject"
                                value={formData.maxTasksPerProject}
                                onChange={handleNumberChange}
                                min="10"
                                max="1000"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="maxFileUploadSize">Max File Upload Size (MB)</label>
                            <input
                                type="number"
                                id="maxFileUploadSize"
                                name="maxFileUploadSize"
                                value={formData.maxFileUploadSize}
                                onChange={handleNumberChange}
                                min="1"
                                max="50"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="apiRateLimit">API Rate Limit (requests/min)</label>
                            <input
                                type="number"
                                id="apiRateLimit"
                                name="apiRateLimit"
                                value={formData.apiRateLimit}
                                onChange={handleNumberChange}
                                min="10"
                                max="1000"
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h2>User Settings</h2>

                    <div className="form-group checkbox-group">
                        <input
                            type="checkbox"
                            id="enablePublicRegistration"
                            name="enablePublicRegistration"
                            checked={formData.enablePublicRegistration}
                            onChange={handleChange}
                        />
                        <label htmlFor="enablePublicRegistration">
                            Enable Public Registration
                        </label>
                    </div>

                    <div className="form-group checkbox-group">
                        <input
                            type="checkbox"
                            id="requireEmailVerification"
                            name="requireEmailVerification"
                            checked={formData.requireEmailVerification}
                            onChange={handleChange}
                        />
                        <label htmlFor="requireEmailVerification">
                            Require Email Verification
                        </label>
                    </div>

                    <div className="form-group">
                        <label htmlFor="defaultUserRole">Default User Role</label>
                        <select
                            id="defaultUserRole"
                            name="defaultUserRole"
                            value={formData.defaultUserRole}
                            onChange={handleChange}
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>

                <div className="settings-section">
                    <h2>Feature Settings</h2>

                    <div className="form-group checkbox-group">
                        <input
                            type="checkbox"
                            id="enableAdvancedAnalytics"
                            name="enableAdvancedAnalytics"
                            checked={formData.enableAdvancedAnalytics}
                            onChange={handleChange}
                        />
                        <label htmlFor="enableAdvancedAnalytics">
                            Enable Advanced Analytics
                        </label>
                    </div>

                    <div className="form-group checkbox-group">
                        <input
                            type="checkbox"
                            id="enableTeamFeatures"
                            name="enableTeamFeatures"
                            checked={formData.enableTeamFeatures}
                            onChange={handleChange}
                        />
                        <label htmlFor="enableTeamFeatures">
                            Enable Team Features
                        </label>
                    </div>
                </div>

                <div className="settings-section">
                    <h2>Email Settings</h2>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="smtpHost">SMTP Host</label>
                            <input
                                type="text"
                                id="smtpHost"
                                name="smtpHost"
                                value={formData.smtpHost}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="smtpPort">SMTP Port</label>
                            <input
                                type="text"
                                id="smtpPort"
                                name="smtpPort"
                                value={formData.smtpPort}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="smtpUser">SMTP Username</label>
                            <input
                                type="text"
                                id="smtpUser"
                                name="smtpUser"
                                value={formData.smtpUser}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="smtpPassword">SMTP Password</label>
                            <input
                                type="password"
                                id="smtpPassword"
                                name="smtpPassword"
                                value={formData.smtpPassword}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="emailFrom">From Email Address</label>
                        <input
                            type="email"
                            id="emailFrom"
                            name="emailFrom"
                            value={formData.emailFrom}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => alert('Test email sent!')}
                        >
                            Test Email
                        </button>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SystemSettings;
